/**
 * Base router class.
 *
 * In addition to some utility functions, this class adds support for a `settings` instance property. This is a mapping from route name to a map from `App` setting name to setting value. This property can be used as follows:
 * ```JavaScript
 * require(['app/routers/RouterBase'], function(RouterBase){
 * 		var Router = RouterBase.extend({
 * 			routes: {
 * 				'app/index': 'index',
 * 				'app/details': 'details'
 * 			},
 *
 * 			settings: {
 * 				'details': {
 * 					'stylesheet': 'Wide'
 * 				}
 * 			},
 *
 * 			index: function(){
 * 				//index
 * 			},
 *
 * 			details: function(){
 * 				//details
 * 			}
 * 		});
 * })
 * ```
 * In the above example, whenever the `'app/details'` route was triggered, the `stylesheet` property of the `App` instance would be set to `'Wide'`. (The `stylesheet` property gets reset for each route invocation.)
 *
 * Before each route invocation, this class sets the `App` layout to "NoMenu", enables scrolling, and sets the `router` property of the `App` instance to `this`. This class also adds an `"enter"` event, fired when navigating to this router from another router (or no router), and a `"leave"` event, fired when navigating from this router to another router.
 * @extends {Backbone.Router}
 * @module core/base/Router
 */
import Backbone from "backbone";
import $ from "jquery";
import _ from "underscore";
import UrlBuilder from "@HBKEngineering/url-builder";
import modals from "core/views/modals";
var loadingTimeout;

var BaseRouter = Backbone.Router.extend(
  {
    /**
     * The current state of the router. Either "initialized", "loading", or "loaded".
     */
    state: "initialized",
    /**
     * Initializes instances of this class.
     *
     *
     * @param  {object} options The options hash.
     * @param {string} [options.root] The root path for the router. All other router paths are formed by prefixing this. If not supplied, the value of the `root` member variable is used.
     * @param {object} [options.layoutOptions] Options to pass to `App.setLayout` when each route is executed. The value of this option can be updated via the `layoutOptions` property.
     * @param {Context} options.context The context associated with this router.
     */
    initialize: function (options) {
      Backbone.Router.prototype.initialize.apply(this, arguments);

      options = options || {};

      if (options.root) {
        this.root = options.root;
      }

      this.options = options;

      this.layoutOptions = options.layoutOptions || this.layoutOptions;
      $(this.getDocument()).on(
        "click",
        'a[href^="' + this.getRootUrl() + '"]',
        _.bind(function (event) {
          var href = $(event.target).attr("href");

          if (href.indexOf(this.getApiRoot()) !== 0) {
            //we should ignore requests to API urls.
            event.preventDefault();
            this.navigate(href, {
              trigger: true,
            });
          }
        }, this)
      );

      this.on(
        "route",
        function (name) {
          var settingSet = _.defaults(
            {},
            this.settings[name],
            this.routerSettings
          );
          var app = this.getApp();

          if (settingSet) {
            _.each(
              settingSet,
              function (value, setting) {
                app[setting] = value;
              },
              this
            );
          }
        },
        this
      );
    },

    /**
     * Gets the document associated with this router.
     * @protected
     * @return {Document} The document.
     */
    getDocument: function () {
      return window.document;
    },

    /**
     * Gets the root URL of this router.
     * @return {String} The normalized, relative root URL
     * @abstract
     */
    getRootUrl: function () {
      return "";
    },

    /**
     * Gets the root URL of api endpoints.
     * @return {String} The relative root api URL.
     * @protected
     * @abstract
     */
    getApiRoot: function () {
      return "";
    },

    /**
     * The settings map.
     * @type {Object}
     */
    settings: {},

    /**
     * The router settings.
     * @type {Object}
     */
    routerSettings: {
      title: false,
    },

    /**
     * Instruct the instance to begin loading data.
     * @return {Promise} A promise which shall resolve when data has been loaded or reject if an error has occurred in loading data. If no asynchronous loading is necessary for this instance, return `null`.
     */
    load: function () {
      return $.when();
    },

    /**
     * Forces a reload of data. (Normally, the `load` function is only called once.)
     * @return {Promise} The result of the subsequent call to `load`.
     */
    reload: function () {
      this.loading();
      var result = (this.deferred = this.load());

      if (this.deferred) {
        result = result.then(
          _.bind(function () {
            this.doneLoading();
          }, this)
        );
      } else {
        this.doneLoading();
      }

      return result;
    },

    _load: function () {
      if (_.isUndefined(this.deferred)) {
        this.deferred = this.load();
      }

      return this.deferred;
    },

    /**
     * Mark the router as being in the "loading" state.
     */
    loading: function () {
      loadingTimeout = setTimeout(function () {
        modals.loading.show();
      }, 250);

      this.state = "loading";
    },

    /**
     * Mark the router as being in the "loaded" state.
     */
    doneLoading: function () {
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
        loadingTimeout = null;
      }

      modals.loading.hide();
      this.state = "loaded";
    },

    /**
     * Gets the URL associated with the given route.
     * @param {string} route The route.
     * @return {String} The normalized route.
     * @protected
     * @abstract
     */
    getRouteUrl: function (route) {
      return "";
    },

    route: function (route, name, callback) {
      Backbone.Router.prototype.route.call(
        this,
        this.getRouteUrl(route),
        name,
        callback
      );
    },

    execute: function (callback, args, name) {
      var app = this.getApp(),
        self = this;
      var _execArgs = arguments;
      var _execute = _.bind(function () {
        app.view = null;
        app.setLayout(app.getDefaultLayout()).done(function () {
          self.doneLoading();

          Backbone.Router.prototype.execute.apply(self, _execArgs);
          self.afterExecute();
        });
      }, this);

      self.beforeExecute();

      this._isRootChanging(BaseRouter.current, this);
      BaseRouter.current = this;

      if (this._load()) {
        this.loading();
        this.deferred.done(_execute);
        this.deferred.fail(
          _.bind(function () {
            //todo use error object
            this.doneLoading();
            this.onLoadError("An unknown error has occurred.");
            _execute();
          }, this)
        );
      } else {
        _execute();
      }
    },

    /**
     * Returns the currently active app instance.
     * @protected
     * @return {core/base/App} The app instance
     */
    getApp: function () {},

    /**
     * A function called when an error occurs during the load of a router.
     * @param err The error.
     */
    onLoadError: function (err) {
      console.error(err);
    },

    /**
     * Called before every route has executed (and the route function has been called).
     */
    beforeExecute: function () {},

    /**
     * Called after every route has executed (and the route function has been called). The default implementation handles updating of the route-specific stylesheet.
     */
    afterExecute: function () {},

    _isRootChanging: function (origRouter, newRouter) {
      if (_.isObject(origRouter)) {
        if (_.isString(origRouter.root) && _.isString(newRouter.root)) {
          //compare the roots of the routers to see if they have changed
          if (origRouter.root != newRouter.root) {
            origRouter.leave(); //Clear the views associated with the old router by triggering an event bound to the windowViews
            newRouter.enter();
          }
        }
      } else if (!_.isObject(origRouter) && _.isObject(newRouter)) {
        newRouter.enter();
      }
    },

    /**
     * Fire the `"enter"` event.
     */
    enter: function () {
      this.trigger("enter");
    },

    /**
     * Fire the `"leave"` event.
     */
    leave: function () {
      this.trigger("leave");
    },

    /**
     * Navigate back to the previous route.
     */
    back: function () {
      Backbone.history.history.back();
    },
  },
  {
    /**
     * The currently active router.
     * @type BaseRouter
     * @static
     */
    current: null,

    /**
     * Navigates to the given path *relative to the root path of the current `App.router`*.
     *
     * To navigate relative to the application root, `require('backbone')` and use
     * ```
     * Backbone.history.navigate(UrlService.url('/some/absolute/path'), {trigger: true});
     * ```
     * @param  {string} path The path to which to navigate.
     * @param  {object} [opts] Options to pass to the Router navigation function.
     * @static
     */
    relativeNavigate: function (path, opts) {
      var navPath = path;
      var options = _.defaults({}, opts, {
        trigger: true,
      });
      var currentRouter = BaseRouter.current;

      if (currentRouter) {
        var root = currentRouter.root;
        var urlBuilder = new UrlBuilder(root);

        if (path && path.substring(0, 1) != "?") {
          navPath = urlBuilder.url("/" + navPath);
        } else if (path === "") {
          navPath = urlBuilder.url("/");
        } else if (path.substring(0, 1) == "?") {
          navPath = urlBuilder.url(navPath);
        }

        currentRouter.navigate(navPath, options);
      }
    },
  }
);

export default BaseRouter;



//////////////////
// WEBPACK FOOTER
// ./public/js/core/base/Router.js
// module id = 93
// module chunks = 1